import { useEffect, useRef } from 'react';

/**
 * Long-press → context-menu fallback for touch devices. Right-click has no
 * touch equivalent, so a ~500 ms press fires `onLongPress(clientX, clientY)` —
 * the same thing the element's `onContextMenu` does on desktop. Cancels if the
 * finger moves past a small threshold (that's a scroll) or lifts early. Only
 * reacts to TOUCH pointers, so mouse users keep the real right-click.
 *
 * Spread the returned handlers onto the element that already has
 * `onContextMenu`. Pair with `select-none [-webkit-touch-callout:none]` on that
 * element so holding doesn't start a native text selection / callout. Opening
 * the menu is idempotent (positioned by the passed coords), so if a touch
 * browser ALSO raises its own `contextmenu` on long-press, the worst case is a
 * harmless re-open at the same spot.
 */
export function useLongPress(onLongPress: (x: number, y: number) => void, ms = 500) {
  const timer = useRef<number | null>(null);
  const releaseGuard = useRef<(() => void) | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    releaseGuard.current?.();
  }, []);

  function cancel() {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }

  // A sheet may appear under the held finger. Consume only the click generated
  // by releasing that same pointer at the same location, never a later action
  // elsewhere or a keyboard click. Otherwise release can activate a menu item.
  function guardRelease(pointerId: number) {
    releaseGuard.current?.();
    let release: { x: number; y: number } | null = null;
    let expiry: number | undefined;
    function cleanup() {
      window.clearTimeout(expiry);
      document.removeEventListener('pointerdown', cleanup, true);
      document.removeEventListener('pointerup', onUp, true);
      document.removeEventListener('pointercancel', onCancel, true);
      document.removeEventListener('click', onClick, true);
      releaseGuard.current = null;
    }
    function onUp(event: PointerEvent) {
      if (event.pointerId !== pointerId) return;
      release = { x: event.clientX, y: event.clientY };
      expiry = window.setTimeout(cleanup, 750);
    }
    function onCancel(event: PointerEvent) { if (event.pointerId === pointerId) cleanup(); }
    function onClick(event: MouseEvent) {
      if (!release || event.detail === 0 || Math.abs(event.clientX - release.x) > 24 || Math.abs(event.clientY - release.y) > 24) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      cleanup();
    }
    document.addEventListener('pointerdown', cleanup, true);
    document.addEventListener('pointerup', onUp, true);
    document.addEventListener('pointercancel', onCancel, true);
    document.addEventListener('click', onClick, true);
    releaseGuard.current = cleanup;
  }

  return {
    onPointerDown(e: React.PointerEvent) {
      if (e.pointerType !== 'touch') return;
      origin.current = { x: e.clientX, y: e.clientY };
      const { clientX, clientY, pointerId } = e;
      cancel();
      timer.current = window.setTimeout(() => {
        guardRelease(pointerId);
        onLongPress(clientX, clientY);
      }, ms);
    },
    onPointerMove(e: React.PointerEvent) {
      const o = origin.current;
      if (!o) return;
      if (Math.abs(e.clientX - o.x) > 10 || Math.abs(e.clientY - o.y) > 10) cancel();
    },
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onPointerLeave: cancel,
  };
}
